import { useCallback } from "react";

import { useEvaluateScope } from "./useEvaluateScope";

import type { PlaygroundEvaluateSheetReference } from "@/queries/playground/evaluate";
import { useAddRows } from "@/queries/smart-tables";
import { displayErrorToast } from "@/utils/toast";

const MAX_EVALUATE_INPUT_SETS = 5;

interface UseAddEvaluateInputSetsArgs {
  mappedVariableKeys: string[];
  reference: PlaygroundEvaluateSheetReference;
  sourceRowCount: number;
  variableSetCount: number;
}

const useAddEvaluateInputSets = ({
  mappedVariableKeys,
  reference,
  sourceRowCount,
  variableSetCount,
}: UseAddEvaluateInputSetsArgs) => {
  const { playground } = useEvaluateScope();
  const { mutateAsync: addBackingRows, isPending: isAddingBackingRows } = useAddRows();
  const canExpandBackingRows =
    reference.source_type === "playground_draft" && !reference.exported_at;
  const maxInputSets = MAX_EVALUATE_INPUT_SETS;
  const remainingInputSets = Math.max(0, maxInputSets - variableSetCount);

  const addInputSets = useCallback(
    async (requestedCount: number): Promise<string[]> => {
      if (isAddingBackingRows || remainingInputSets === 0) {
        return [];
      }

      const count = Math.min(Math.max(requestedCount, 1), remainingInputSets);
      const backingRowsToAdd = canExpandBackingRows
        ? Math.max(0, variableSetCount + count - sourceRowCount)
        : 0;

      try {
        if (backingRowsToAdd > 0) {
          await addBackingRows({
            tableId: reference.smart_table_id,
            sheetId: reference.sheet_id,
            count: backingRowsToAdd,
          });
        }

        return Array.from({ length: count }, () =>
          playground.addEmptyInputVariableSet(mappedVariableKeys),
        );
      } catch (error) {
        displayErrorToast(error instanceof Error ? error.message : "Failed to add input sets.");
        return [];
      }
    },
    [
      addBackingRows,
      canExpandBackingRows,
      isAddingBackingRows,
      mappedVariableKeys,
      playground,
      reference.sheet_id,
      reference.smart_table_id,
      remainingInputSets,
      sourceRowCount,
      variableSetCount,
    ],
  );

  return {
    addInputSets,
    isAddingInputSets: isAddingBackingRows,
    maxInputSets,
    remainingInputSets,
  };
};

export default useAddEvaluateInputSets;
